import { useState } from "react";
import { motion } from "framer-motion";
import { api, apiError } from "@/lib/api";
import { Nav } from "@/components/marketing/Nav";
import { CtaFooter } from "@/components/marketing/CtaFooter";
import { ResearchBrochure } from "@/components/marketing/ResearchBrochure";
import { Reveal } from "@/components/Reveal";

const inputCls =
  "w-full bg-white border border-navy/15 px-4 py-3 text-sm text-navy focus:outline-none focus:border-gold transition-colors";
const labelCls = "font-mono text-[10px] tracking-[0.25em] uppercase text-navy/50 block mb-2";

const SERVICES = [
  "Research Echocardiography",
  "Core Lab / Blinded Reads",
  "ECG / ABPM for Studies",
  "Full Study Support",
];

const initial = { name: "", organization: "", role: "", email: "", phone: "", service: SERVICES[0], message: "" };

const Section = ({ title, children, testId }) => (
  <Reveal>
    <div className="border-t border-navy/10 pt-10 mt-16" data-testid={testId}>
      <h2 className="font-serif text-2xl lg:text-3xl font-semibold text-navy mb-6">{title}</h2>
      <div className="space-y-5 text-sm lg:text-base text-navy/60 leading-relaxed max-w-3xl">
        {children}
      </div>
    </div>
  </Reveal>
);

const audiences = [
  "NIHR-funded non-commercial studies and NIHR portfolio research",
  "R&D and research delivery teams needing cardiac physiology capacity they cannot recruit to",
  "Sites supported by their Regional Research Delivery Network, anywhere in England",
  "Universities and academic sponsors",
  "CROs and commercial sponsors running cardiovascular endpoints in the UK",
  "Primary care and community research sites without on-site cardiac diagnostics",
];

export default function ClinicalResearch() {
  const [form, setForm] = useState(initial);
  const [error, setError] = useState("");
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setSending(true);
    try {
      await api.post("/contact", {
        name: form.name,
        organization: form.organization,
        role: form.role,
        email: form.email,
        phone: form.phone,
        enquiry_type: `Clinical Research — ${form.service}`,
        message: form.message,
      });
      setDone(true);
    } catch (err) {
      setError(apiError(err));
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="bg-ivory min-h-screen" data-testid="clinical-research-page">
      <Nav />

      <section className="pt-32 pb-16 max-w-7xl mx-auto px-6 lg:px-10">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
        >
          <p className="font-mono text-[11px] tracking-[0.35em] uppercase text-gold mb-4">
            Imaging Support for NIHR-Funded Studies, R&D Departments, Sponsors & CROs
          </p>
          <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl font-bold text-[#4169E1] leading-tight max-w-4xl" data-testid="cr-title">
            Clinical Research and Research Echocardiography
          </h1>
          <p className="mt-8 text-base text-navy/60 leading-relaxed max-w-2xl">
            Studies lose participants while they wait. A volunteer consents, then waits
            weeks for a screening echocardiogram booked into a busy clinical list, and by
            the time the appointment arrives the visit window has passed, the participant
            has changed their mind, or the study has moved on without them. Recruitment
            targets slip for reasons that have nothing to do with the science.
          </p>
          <p className="mt-5 text-base text-navy font-medium leading-relaxed max-w-2xl border-l-2 border-gold pl-5">
            We can provide dedicated research imaging sessions, booked around a study's
            visit windows rather than around a clinical waiting list, so that screening
            and follow-up scans happen when the protocol says they should.
          </p>
        </motion.div>
      </section>

      <ResearchBrochure />

      <div className="max-w-7xl mx-auto px-6 lg:px-10 pb-10">
        <Reveal>
          <div className="border-t border-navy/10 pt-10 mt-4" data-testid="cr-audiences">
            <h2 className="font-serif text-2xl lg:text-3xl font-semibold text-navy mb-8">
              Who we can support
            </h2>
            <ul className="space-y-4 max-w-3xl">
              {audiences.map((a) => (
                <li key={a} className="flex gap-4 text-sm lg:text-base text-navy/60 leading-relaxed">
                  <span className="text-gold shrink-0 pt-0.5">✦</span>
                  {a}
                </li>
              ))}
            </ul>
          </div>
        </Reveal>

        <Section title="Acquisition" testId="cr-acquisition">
          <p>
            Protocol-specific transthoracic acquisition to sponsor-defined image sets.
            Standard and advanced measurements including left ventricular and left atrial
            strain, diastolic function grading, right ventricular assessment and valve
            quantification.
          </p>
          <p>
            Screening, baseline and follow-up visit scanning. ECG, ambulatory ECG and
            ambulatory blood pressure monitoring where required by protocol.
          </p>
        </Section>

        <Section title="Reading and core lab work" testId="cr-corelab">
          <p>
            Blinded independent review of study echocardiograms. Standardised measurement
            to a study-specific analysis manual. Inter- and intra-observer variability
            assessment on a defined sample. Query resolution and re-read on request. Across any
            sites covered, every study would be acquired and reported by the same
            accredited practitioner, removing inter-operator variability from that part
            of the dataset.
          </p>
        </Section>

        <Section title="Data and compliance" testId="cr-compliance">
          <p>
            GCP-trained operator. Source data recorded to ALCOA+ principles with a
            documented audit trail. DICOM pseudonymisation and secure transfer according
            to agreed study arrangements. Protocol deviation logging and sponsor
            notification. Documentation prepared for sponsor monitoring and audit.
          </p>
        </Section>

        <Section title="Costing and set-up" testId="cr-costing">
          <p>
            A costed schedule of our activity can be provided at feasibility stage, so that
            imaging is correctly attributed on the SoECAT and captured as a research cost
            rather than absorbed by the site. The constraints of research delivery —
            capacity gaps, competing clinical lists, and a visit window that does not
            move — are familiar ones.
          </p>
        </Section>

        <Section title="Background" testId="cr-background">
          <p>
            The service is led by a practitioner working within a clinical research unit
            in experimental medicine, with experience of CTIMP delivery including
            early-phase immunotherapy trials, and a publication record in
            echocardiographic methodology covering left atrial mechanics and heart
            failure with preserved ejection fraction. Research imaging is a primary
            interest rather than an extension of a clinical service.
          </p>
        </Section>
      </div>

      <section id="enquire" className="bg-navy py-24 mt-16 scroll-mt-24" data-testid="cr-enquiry-section">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 grid lg:grid-cols-12 gap-14">
          <div className="lg:col-span-5">
            <Reveal>
              <p className="font-mono text-[11px] tracking-[0.35em] uppercase text-gold mb-4">
                Contact Us
              </p>
              <h2 className="font-serif text-2xl sm:text-3xl lg:text-4xl font-semibold text-ivory leading-tight mb-6">
                Tell us about your study.
              </h2>
              <p className="text-sm text-ivory/60 leading-relaxed max-w-md">
                Share the study outline, funder, sites and imaging requirements, and we
                will respond within one working day — with a costed schedule at
                feasibility stage where useful.
              </p>
              <p className="mt-6 text-xs text-ivory/40 leading-relaxed max-w-md border-l-2 border-gold/50 pl-4">
                Please do not include any patient-identifiable information in this form.
              </p>
            </Reveal>
          </div>

          <div className="lg:col-span-7">
            {done ? (
              <div className="border border-gold/50 bg-ivory p-10" data-testid="cr-form-success">
                <h3 className="font-serif text-2xl font-semibold text-navy mb-3">Enquiry received.</h3>
                <p className="text-sm text-navy/60 leading-relaxed">
                  Thank you — the CVI research team will review your study outline and
                  respond within one working day.
                </p>
              </div>
            ) : (
              <form onSubmit={submit} className="grid sm:grid-cols-2 gap-5" data-testid="cr-enquiry-form">
                <div>
                  <label className={`${labelCls} !text-ivory/50`}>Name</label>
                  <input required value={form.name} onChange={set("name")} data-testid="cr-name-input" className={inputCls} placeholder="Dr Jane Smith" />
                </div>
                <div>
                  <label className={`${labelCls} !text-ivory/50`}>Organisation</label>
                  <input required value={form.organization} onChange={set("organization")} data-testid="cr-organization-input" className={inputCls} placeholder="University / CRO / NHS R&D" />
                </div>
                <div>
                  <label className={`${labelCls} !text-ivory/50`}>Role</label>
                  <input value={form.role} onChange={set("role")} data-testid="cr-role-input" className={inputCls} placeholder="Study Coordinator / Sponsor Lead" />
                </div>
                <div>
                  <label className={`${labelCls} !text-ivory/50`}>Email</label>
                  <input type="email" required value={form.email} onChange={set("email")} data-testid="cr-email-input" className={inputCls} placeholder="you@organisation.ac.uk" />
                </div>
                <div>
                  <label className={`${labelCls} !text-ivory/50`}>Telephone</label>
                  <input type="tel" value={form.phone} onChange={set("phone")} data-testid="cr-phone-input" className={inputCls} placeholder="07…" />
                </div>
                <div>
                  <label className={`${labelCls} !text-ivory/50`}>Support Required</label>
                  <select value={form.service} onChange={set("service")} data-testid="cr-service-select" className={inputCls}>
                    {SERVICES.map((s) => (
                      <option key={s}>{s}</option>
                    ))}
                  </select>
                </div>
                <div className="sm:col-span-2">
                  <label className={`${labelCls} !text-ivory/50`}>Message</label>
                  <textarea rows={5} required value={form.message} onChange={set("message")} data-testid="cr-message-input" className={inputCls} placeholder="Study title, funder, sites, expected recruitment, imaging requirements…" />
                </div>

                {error && (
                  <p data-testid="cr-form-error" className="sm:col-span-2 text-sm text-crimson border border-crimson/30 bg-crimson/5 px-4 py-3">
                    {error}
                  </p>
                )}

                <button
                  type="submit"
                  disabled={sending}
                  data-testid="cr-submit-button"
                  className="sm:col-span-2 w-full bg-gold text-navy font-mono text-xs tracking-[0.25em] uppercase py-4 hover:bg-champagne transition-colors disabled:opacity-50"
                >
                  {sending ? "Sending…" : "Contact Us"}
                </button>
              </form>
            )}
          </div>
        </div>
      </section>

      <CtaFooter />
    </div>
  );
}
